import React, { useEffect } from 'react';

import cx from '../../utils/classnames';

import styles from './videoPlayer.module.scss';

type VideoPlayerProps = {
  autoplay?: boolean;
  className?: string;
  hasControls?: boolean;
  height?: number;
  orderNumber?: number /* To differentiate the video player instance */;
  refId: string;
  serviceId: string;
  videoId?: string;
  width?: number;
  onLoad?: () => void;
  onError?: () => void;
};

const noop = () => {};

const BASE_URL = 'https://stream.cms.rakuten.co.jp/gate/play_pf/';

function VideoPlayer({
  autoplay = false,
  className,
  hasControls = false,
  height,
  orderNumber,
  refId,
  serviceId,
  videoId,
  width,
  onLoad = noop,
  onError = noop,
}: VideoPlayerProps) {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const targetElementId = React.useRef(
    orderNumber
      ? `vpf-${videoId}-${orderNumber}`
      : `vpf-${videoId}-${Math.random().toString(36).slice(-5)}`,
  ).current;

  useEffect(() => {
    const embedVideoPlayer = () => {
      if (wrapperRef.current) {
        const wrapperHeight = wrapperRef.current.clientHeight;
        const wrapperWidth = wrapperRef.current.clientWidth;

        const params: Record<string, string | undefined> = {
          ref_id: refId,
          sid: serviceId,
          mid: videoId,
          sel: `#${targetElementId}`,
          w: width?.toString() || wrapperWidth.toString(),
          h: height?.toString() || wrapperHeight.toString(),
          ctr: hasControls ? '1' : undefined,
          a: autoplay ? '1' : undefined,
        };

        const scriptUrl = new URL(BASE_URL);
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            scriptUrl.searchParams.set(key, value);
          }
        });

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = scriptUrl.toString();
        script.onload = onLoad;
        script.onerror = onError;

        wrapperRef.current.appendChild(script);
      }
    };
    /**
     * Initialize video player after the parent is fully painted on browser
     * to match parent element's width and height
     */
    if (typeof window !== 'undefined') {
      /* Polyfill for requestIdleCallback */
      if (!window.requestIdleCallback) {
        window.requestIdleCallback = function (callback, options) {
          const start = Date.now();
          const timeout = options && typeof options.timeout === 'number' ? options.timeout : 50;
          let didTimeout = false;

          const runCallback = () => {
            didTimeout =
              !!options &&
              typeof options.timeout === 'number' &&
              Date.now() - start >= options.timeout;
            const deadline = {
              didTimeout,
              timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
            };
            callback(deadline);
          };

          const timerId = setTimeout(runCallback, timeout) as unknown as number;
          return timerId;
        };

        window.cancelIdleCallback = function (id) {
          clearTimeout(id);
        };
      }
      /* end of polyfill */
      window.requestIdleCallback(embedVideoPlayer);
    }
  }, [autoplay, hasControls, height, refId, serviceId, targetElementId, width]);

  return (
    <div
      ref={wrapperRef}
      data-testid="vpf-player"
      id={targetElementId}
      className={cx(styles.wrapper, className)}
    ></div>
  );
}

export default VideoPlayer;
